import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { PageHeader } from "../../components/domain/Bits";
import { Button } from "../../components/ui/button";
import { EventForm } from "../../components/domain/EventForm";
import { EventItem, EventShape } from "../../components/domain/EventList";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { CalendarPlus, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";

type FeedItem = {
  kind: "event" | "task" | "follow_up_task";
  id: string;
  title: string;
  event_type_code?: string | null;
  task_type_code?: string | null;
  all_day: boolean;
  start_at?: string | null;
  end_at?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  location?: string | null;
  status: string;
  assigned_to_user_id: string;
  contact_id?: string | null;
  organization_id?: string | null;
  opportunity_id?: string | null;
  priority?: string;
};

type FeedResponse = {
  items: FeedItem[];
  from_utc: string;
  to_utc: string;
  tenant_timezone: string;
};

type ViewMode = "agenda" | "week" | "month";

function startOfWeek(d: Date): Date {
  const day = d.getDay(); // 0 = Sunday
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  out.setDate(d.getDate() - day);
  return out;
}
function startOfMonth(d: Date): Date {
  const out = new Date(d.getFullYear(), d.getMonth(), 1);
  out.setHours(0, 0, 0, 0);
  return out;
}
function addDays(d: Date, n: number): Date {
  const out = new Date(d);
  out.setDate(d.getDate() + n);
  return out;
}
function fmtDate(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
export function dateKeyInTimezone(value: string | Date, timezone: string): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone || "UTC", year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(value instanceof Date ? value : new Date(value));
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value || "";
  return `${part("year")}-${part("month")}-${part("day")}`;
}
function itemDateKey(it: FeedItem, timezone: string): string {
  if (it.all_day) return it.start_date || "";
  if (!it.start_at) return "";
  return dateKeyInTimezone(it.start_at, timezone);
}
export function itemTimeLabel(it: FeedItem, timezone: string): string {
  if (it.all_day) return "All day";
  if (!it.start_at) return "";
  const s = new Date(it.start_at);
  const e = it.end_at ? new Date(it.end_at) : null;
  const timeFmt = new Intl.DateTimeFormat(undefined, {
    hour: "numeric", minute: "2-digit", timeZone: timezone || "UTC",
  });
  return e ? `${timeFmt.format(s)} – ${timeFmt.format(e)}` : timeFmt.format(s);
}
function itemColor(it: FeedItem): string {
  if (it.kind === "event") {
    if (it.status === "cancelled") return "bg-slate-100 border-slate-300 text-slate-500 line-through";
    if (it.status === "completed") return "bg-green-50 border-green-200 text-green-800";
    if (it.status === "no_show") return "bg-red-50 border-red-200 text-red-800";
    return "bg-sky-50 border-sky-200 text-sky-900";
  }
  if (it.kind === "follow_up_task") return "bg-amber-50 border-amber-200 text-amber-900";
  return "bg-slate-50 border-slate-200 text-slate-800";
}
function kindLabel(it: FeedItem): string {
  if (it.kind === "event") return "Event";
  if (it.kind === "follow_up_task") return "Follow-Up";
  return "Task";
}

export default function CalendarPage() {
  const { user } = useAuth();
  const [view, setView] = useState<ViewMode>("agenda");
  const [cursor, setCursor] = useState<Date>(() => new Date());
  const [feed, setFeed] = useState<FeedItem[]>([]);
  const [tenantTz, setTenantTz] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [addOpen, setAddOpen] = useState<boolean>(false);
  const [selectedEvent, setSelectedEvent] = useState<EventShape | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const canMutate = user?.tenant_role !== "viewer";

  const { rangeStart, rangeEnd } = useMemo(() => {
    if (view === "agenda") {
      const s = new Date(cursor); s.setHours(0, 0, 0, 0);
      const e = addDays(s, 7); e.setHours(23, 59, 59, 999);
      return { rangeStart: s, rangeEnd: e };
    }
    if (view === "week") {
      const s = startOfWeek(cursor);
      const e = addDays(s, 7); e.setHours(23, 59, 59, 999);
      return { rangeStart: s, rangeEnd: e };
    }
    // month: grid runs Sun-of-first-week-of-month → Sat-of-last-week
    const s = startOfWeek(startOfMonth(cursor));
    const eBase = addDays(startOfMonth(addDays(startOfMonth(cursor), 32)), -1);
    const e = addDays(startOfWeek(eBase), 7); e.setHours(23, 59, 59, 999);
    return { rangeStart: s, rangeEnd: e };
  }, [view, cursor]);

  const refetch = useCallback(async () => {
    setError(""); setLoading(true);
    try {
      const r = await api.get<FeedResponse>("/crm/calendar/feed", {
        params: {
          from_utc: rangeStart.toISOString(),
          to_utc: rangeEnd.toISOString(),
        },
      });
      setFeed(r.data.items || []);
      setTenantTz(r.data.tenant_timezone || "");
    } catch (e: any) {
      setError("Could not load calendar.");
    } finally {
      setLoading(false);
    }
  }, [rangeStart, rangeEnd]);

  useEffect(() => { refetch(); }, [refetch]);

  const openItem = useCallback(async (item: FeedItem) => {
    if (item.kind !== "event") return;
    setDetailLoading(true);
    try {
      const { data } = await api.get<EventShape>(`/crm/events/${item.id}`);
      setSelectedEvent(data);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const groupedByDate = useMemo(() => {
    const m = new Map<string, FeedItem[]>();
    for (const it of feed) {
      const k = itemDateKey(it, tenantTz || "UTC");
      if (!k) continue;
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(it);
    }
    return m;
  }, [feed, tenantTz]);

  function stepBy(n: number) {
    if (view === "agenda") setCursor((d) => addDays(d, 7 * n));
    else if (view === "week") setCursor((d) => addDays(d, 7 * n));
    else setCursor((d) => new Date(d.getFullYear(), d.getMonth() + n, 1));
  }

  const headerSubtitle = tenantTz
    ? `Times shown in ${tenantTz}`
    : "Combined events, tasks, and follow-ups";

  return (
    <>
      <PageHeader title="Calendar" subtitle={headerSubtitle} />
      <div className="max-w-7xl mx-auto p-4 md:p-8">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-1" data-testid="calendar-view-switcher">
            {(["agenda", "week", "month"] as ViewMode[]).map((v) => (
              <Button key={v} variant={v === view ? "default" : "outline"} size="sm"
                onClick={() => setView(v)}
                data-testid={`calendar-view-${v}`}>
                {v[0].toUpperCase() + v.slice(1)}
              </Button>
            ))}
          </div>
          <div className="flex items-center gap-1" data-testid="calendar-navigator">
            <Button variant="outline" size="sm" onClick={() => stepBy(-1)}
              data-testid="calendar-prev-btn"
              aria-label="Previous period">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setCursor(new Date())}
              data-testid="calendar-today-btn">Today</Button>
            <Button variant="outline" size="sm" onClick={() => stepBy(1)}
              data-testid="calendar-next-btn"
              aria-label="Next period">
              <ChevronRight className="w-4 h-4" />
            </Button>
            <span className="ml-3 text-sm font-medium text-slate-700"
                  data-testid="calendar-range-label">
              {view === "month"
                ? cursor.toLocaleDateString(undefined, { month: "long", year: "numeric" })
                : `${rangeStart.toLocaleDateString()} – ${rangeEnd.toLocaleDateString()}`}
            </span>
          </div>
          {canMutate ? (
            <Button size="sm" onClick={() => setAddOpen(true)}
              data-testid="calendar-schedule-btn">
              <CalendarPlus className="w-4 h-4 mr-1" /> Schedule
            </Button>
          ) : (
            <div className="text-xs text-slate-500" data-testid="calendar-read-only-note">
              Read-only view.
            </div>
          )}
        </div>

        {loading ? (
          <div className="text-sm text-slate-500 flex items-center gap-2"
               role="status" aria-busy="true"
               data-testid="calendar-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading calendar…
          </div>
        ) : error ? (
          <div role="alert" className="text-sm text-red-600" data-testid="calendar-error">
            {error} <Button variant="outline" size="sm" onClick={refetch}
              className="ml-2" data-testid="calendar-retry-btn">Retry</Button>
          </div>
        ) : (
          <>
            {/* Mobile always shows agenda regardless of desktop view. */}
            <div className="md:hidden" data-testid="calendar-mobile-agenda">
              <AgendaView cursor={cursor} rangeStart={rangeStart} rangeEnd={rangeEnd}
                groupedByDate={groupedByDate} onSelect={openItem} timezone={tenantTz || "UTC"} />
            </div>
            <div className="hidden md:block">
              {view === "agenda" ? (
                <AgendaView cursor={cursor} rangeStart={rangeStart} rangeEnd={rangeEnd}
                  groupedByDate={groupedByDate} onSelect={openItem} timezone={tenantTz || "UTC"} />
              ) : view === "week" ? (
                <WeekView rangeStart={rangeStart} groupedByDate={groupedByDate} onSelect={openItem} timezone={tenantTz || "UTC"} />
              ) : (
                <MonthView cursor={cursor} groupedByDate={groupedByDate} onSelect={openItem} timezone={tenantTz || "UTC"} />
              )}
            </div>
          </>
        )}
      </div>

      {canMutate && user ? (
        <EventForm open={addOpen} onOpenChange={setAddOpen}
          onSuccess={() => refetch()}
          currentUserId={user.id} />
      ) : null}
      <Dialog open={!!selectedEvent || detailLoading} onOpenChange={(open) => !open && setSelectedEvent(null)}>
        <DialogContent data-testid="calendar-event-detail">
          <DialogHeader>
            <DialogTitle>Calendar event details</DialogTitle>
            <DialogDescription>Update the actual visit status and open linked CRM records.</DialogDescription>
          </DialogHeader>
          {detailLoading ? <div className="text-sm text-slate-500">Loading event…</div> : selectedEvent ? (
            <div className="space-y-3">
              <EventItem event={selectedEvent} onRefetch={async () => {
                const { data } = await api.get<EventShape>(`/crm/events/${selectedEvent.id}`);
                setSelectedEvent(data); refetch();
              }} />
              <div className="flex flex-wrap gap-2 text-sm">
                {selectedEvent.contact_id ? <a className="text-sky-700 underline" href={`/contacts/${selectedEvent.contact_id}`}>Open related contact</a> : null}
                {selectedEvent.opportunity_id ? <a className="text-sky-700 underline" href={`/opportunities/${selectedEvent.opportunity_id}`}>Open related opportunity</a> : null}
                {selectedEvent.organization_id ? <a className="text-sky-700 underline" href="/organizations">Open related clinic</a> : null}
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}

const AgendaView: React.FC<{
  cursor: Date; rangeStart: Date; rangeEnd: Date;
  groupedByDate: Map<string, FeedItem[]>;
  onSelect: (item: FeedItem) => void; timezone: string;
}> = ({ rangeStart, rangeEnd, groupedByDate, onSelect, timezone }) => {
  const days: Date[] = [];
  const cur = new Date(rangeStart);
  while (cur <= rangeEnd) {
    days.push(new Date(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return (
    <div className="space-y-4" data-testid="calendar-agenda">
      {days.map((d) => {
        const k = fmtDate(d);
        const items = groupedByDate.get(k) || [];
        const isToday = dateKeyInTimezone(new Date(), timezone) === k;
        return (
          <div key={k} className={`border ${isToday ? "border-sky-400" : "border-slate-200"} rounded-lg bg-white p-3`}
               data-testid={`calendar-agenda-day-${k}`}>
            <div className="text-sm font-semibold text-slate-800">
              {d.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}
              {isToday ? <span className="ml-2 text-xs text-sky-600" data-testid={`calendar-today-marker-${k}`}>Today</span> : null}
            </div>
            {items.length === 0 ? (
              <div className="text-xs text-slate-500 mt-1" data-testid={`calendar-agenda-empty-${k}`}>
                Nothing scheduled.
              </div>
            ) : (
              <ul className="mt-2 space-y-1.5">
                {items.map((it) => (
                  <li key={`${it.kind}-${it.id}`}
                      onClick={() => onSelect(it)}
                      className={`text-xs px-2 py-1.5 rounded border ${itemColor(it)} ${it.kind === "event" ? "cursor-pointer hover:ring-2 hover:ring-sky-300" : ""}`}
                      data-testid={`calendar-item-${it.kind}-${it.id}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="uppercase tracking-wider text-[10px] opacity-70">{kindLabel(it)}</span>
                      <span className="font-medium">{it.title}</span>
                    </div>
                    <div className="opacity-70 mt-0.5">{itemTimeLabel(it, timezone)}{it.location ? ` · ${it.location}` : ""}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
};

const WeekView: React.FC<{
  rangeStart: Date; groupedByDate: Map<string, FeedItem[]>;
  onSelect: (item: FeedItem) => void; timezone: string;
}> = ({ rangeStart, groupedByDate, onSelect, timezone }) => {
  const days: Date[] = [];
  for (let i = 0; i < 7; i++) days.push(addDays(rangeStart, i));
  const todayKey = dateKeyInTimezone(new Date(), timezone);
  return (
    <div className="grid grid-cols-7 gap-2" data-testid="calendar-week">
      {days.map((d) => {
        const k = fmtDate(d);
        const items = groupedByDate.get(k) || [];
        return (
          <div key={k}
               className={`border rounded-lg bg-white p-2 min-h-[8rem]
                 ${todayKey === k ? "border-sky-400" : "border-slate-200"}`}
               data-testid={`calendar-week-day-${k}`}>
            <div className="text-xs font-semibold text-slate-800">
              {d.toLocaleDateString(undefined, { weekday: "short" })} {d.getDate()}
            </div>
            <ul className="mt-2 space-y-1">
              {items.map((it) => (
                <li key={`${it.kind}-${it.id}`}
                    onClick={() => onSelect(it)}
                    className={`text-[11px] px-1.5 py-1 rounded border ${itemColor(it)}`}
                    data-testid={`calendar-item-${it.kind}-${it.id}`}
                    title={it.title}>
                  <div className="truncate font-medium">{it.title}</div>
                  <div className="opacity-70 truncate">{itemTimeLabel(it, timezone)}</div>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
};

const MonthView: React.FC<{
  cursor: Date; groupedByDate: Map<string, FeedItem[]>;
  onSelect: (item: FeedItem) => void; timezone: string;
}> = ({ cursor, groupedByDate, onSelect, timezone }) => {
  const monthStart = startOfMonth(cursor);
  const gridStart = startOfWeek(monthStart);
  const days: Date[] = [];
  for (let i = 0; i < 42; i++) days.push(addDays(gridStart, i));
  const todayKey = dateKeyInTimezone(new Date(), timezone);
  return (
    <div className="grid grid-cols-7 gap-1" data-testid="calendar-month">
      {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => (
        <div key={d} className="text-[10px] uppercase tracking-widest text-slate-500 px-1">{d}</div>
      ))}
      {days.map((d) => {
        const k = fmtDate(d);
        const items = groupedByDate.get(k) || [];
        const isThisMonth = d.getMonth() === cursor.getMonth();
        return (
          <div key={k}
               className={`border rounded bg-white p-1 min-h-[5.5rem] text-xs
                 ${todayKey === k ? "border-sky-500" : "border-slate-200"}
                 ${!isThisMonth ? "opacity-50" : ""}`}
               data-testid={`calendar-month-day-${k}`}>
            <div className="text-[11px] font-semibold text-slate-800">{d.getDate()}</div>
            <ul className="mt-0.5 space-y-0.5">
              {items.slice(0, 3).map((it) => (
                <li key={`${it.kind}-${it.id}`}
                    onClick={() => onSelect(it)}
                    className={`px-1 py-0.5 rounded border text-[10px] ${itemColor(it)}`}
                    data-testid={`calendar-item-${it.kind}-${it.id}`}
                    title={`${it.title} ${itemTimeLabel(it, timezone)}`}>
                  <span className="truncate block">{it.title}</span>
                </li>
              ))}
              {items.length > 3 ? (
                <li className="text-[10px] text-slate-500 pl-1"
                    data-testid={`calendar-month-day-${k}-more`}>
                  +{items.length - 3} more
                </li>
              ) : null}
            </ul>
          </div>
        );
      })}
    </div>
  );
};
